// frontend/src/pages/BatchPage.jsx
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import toast from "react-hot-toast";
import { importStream, downloadTemplate } from "../api/codes";
import { getUsage } from "../api/orgs";
import { QR_TYPES } from "../components/QrPayloadFields";
import BatchCsvTab from "./BatchCsvTab";

const FREE_PLANS = new Set(["free", "starter"]);
const LARGE_FILE_THRESHOLD = 1000;

/**
 * BatchPage — bulk-import QR codes from a CSV or XLSX file via the SSE
 * streaming endpoint with live progress, row-error list, and plan gate.
 */
function BatchPage() {
  const [csvFile, setCsvFile] = useState(null);
  const [csvRowCount, setCsvRowCount] = useState(null);
  const [csvType, setCsvType] = useState("url");
  const [importMode, setImportMode] = useState("create");
  const [submitting, setSubmitting] = useState(false);

  // SSE state
  const [processed, setProcessed] = useState(0);
  const [rowErrors, setRowErrors] = useState([]);
  const [complete, setComplete] = useState(null); // { inserted, errors }

  // Plan gate
  const [planName, setPlanName] = useState(null);

  useEffect(() => {
    getUsage()
      .then((u) => setPlanName(u?.plan?.name ?? null))
      .catch(() => {});
  }, []);

  const isFreePlan = planName !== null && FREE_PLANS.has(planName.toLowerCase());
  const largeFileWarning =
    isFreePlan && csvRowCount !== null && csvRowCount > LARGE_FILE_THRESHOLD;

  const handleDownloadTemplate = async () => {
    try {
      await downloadTemplate(csvType);
    } catch {
      toast.error("Failed to download template.");
    }
  };

  const handleSubmit = async () => {
    if (!csvFile) {
      toast.error("Choose a file first.");
      return;
    }
    setSubmitting(true);
    setProcessed(0);
    setRowErrors([]);
    setComplete(null);

    try {
      await importStream(csvFile, csvType, importMode, {
        onProgress: (data) => {
          setProcessed((n) => n + (data.rows_in_batch ?? 0));
        },
        onRowError: (err) => {
          setRowErrors((prev) => [...prev, err]);
        },
        onComplete: (data) => {
          setComplete(data);
          if (data.inserted > 0) {
            toast.success(`${data.inserted} code(s) imported.`);
          }
        },
        onError: (data) => {
          toast.error(data.message ?? "Import failed.");
        },
      });
    } catch (err) {
      if (err.status === 402) {
        toast.error("Large file import requires Pro or Business plan.");
      } else {
        toast.error(err.message ?? "Import failed. Please try again.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  const total = csvRowCount ?? processed;

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between gap-4">
        <h1 className="text-xl font-semibold text-gray-900">Import QR Codes</h1>
        <button
          onClick={handleDownloadTemplate}
          className="text-sm text-blue-600 hover:text-blue-800 hover:underline"
        >
          ↓ Download {csvType} template CSV
        </button>
      </div>

      {/* Card */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        <div className="p-5 space-y-4">
          <BatchCsvTab
            file={csvFile}
            rowCount={csvRowCount}
            qrType={csvType}
            onTypeChange={setCsvType}
            types={QR_TYPES}
            onFileChange={(f, count) => {
              setCsvFile(f);
              setCsvRowCount(count);
              setComplete(null);
              setRowErrors([]);
              setProcessed(0);
            }}
            mode={importMode}
            onModeChange={setImportMode}
          />

          {/* Large-file plan gate notice */}
          {largeFileWarning && (
            <div className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
              <span className="mt-0.5">⚠</span>
              <span>
                Large file import ({csvRowCount.toLocaleString()} rows) requires
                Pro or higher.{" "}
                <Link to="/billing" className="font-medium underline">
                  Upgrade plan
                </Link>
              </span>
            </div>
          )}

          {/* Progress bar (visible while streaming) */}
          {submitting && (
            <div>
              <div className="flex justify-between text-xs text-gray-500 mb-1">
                <span>
                  {processed}
                  {total > 0 && ` / ${total}`} rows processed
                </span>
                {total > 0 && (
                  <span>{Math.min(100, Math.round((processed / total) * 100))}%</span>
                )}
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div
                  className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                  style={{
                    width: total > 0 ? `${Math.min(100, (processed / total) * 100)}%` : "0%",
                  }}
                />
              </div>
            </div>
          )}

          {/* Live row-error list */}
          {rowErrors.length > 0 && (
            <div className="rounded-lg border border-red-200 bg-red-50 p-3 space-y-1">
              <p className="text-sm font-medium text-red-700">Validation errors:</p>
              <ul className="text-sm text-red-600 space-y-0.5 max-h-40 overflow-y-auto">
                {rowErrors.map((e, i) => (
                  <li key={i}>
                    Row {e.row} — {e.field} — {e.message}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Complete banner */}
          {complete && (
            <div
              className={`rounded-lg border px-4 py-3 text-sm ${
                complete.errors > 0
                  ? "border-yellow-200 bg-yellow-50 text-yellow-800"
                  : "border-green-200 bg-green-50 text-green-800"
              }`}
            >
              {complete.inserted > 0 && (
                <div>✓ {complete.inserted} code(s) imported successfully.</div>
              )}
              {complete.errors > 0 && (
                <div>⚠ {complete.errors} row(s) had errors and were skipped.</div>
              )}
              {complete.inserted === 0 && complete.errors === 0 && (
                <div>No rows were imported.</div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-3 bg-gray-50 border-t border-gray-200 flex justify-end">
          <button
            onClick={handleSubmit}
            disabled={submitting || !csvFile}
            className="px-5 py-2 rounded-lg bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {submitting ? "Importing…" : "Import CSV"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default BatchPage;
